import { formatStructureDate } from '../../utils/formatStructureDate';
import { processStatusUrl } from '../../utils/processStatusLink';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { uploadAndExtractFloorplan } from '../../services/floorplanExtractionService';
import ChooseFloorPlanModal from '../ChooseFloorPlanModal';
import {
  convertToIFCCloud,
  saveIFCConversion,
  convertToDXFCloud,
} from '../../services/structureService';
import {
  getPreTransformedRoomsForMultipleStructures,
  exportStructureToJSON,
} from '../../services/preTransformedStructureService';
import ProjectAssignments from '../ProjectAssignments';
import { useLoading } from '../../contexts/LoadingContext';
import { getRoomType } from '../../utils/floorPlanUtils';
import { roomNameEditSeed } from '../../utils/roomNameEdit';
import { roomDisplayName } from '@shared-lib/rooms/roomDisplayName';
import { createCallable } from '../../firebaseFunctions';
import IOSAppDialog from '../IOSAppDialog';
import AIRenderingsGrid from '../AIRenderingsGrid';
import PreTransformedViewer from '../PreTransformedViewer';
import IFCExportDialog from '../IFCExportDialog';
import DXFExportDialog from '../DXFExportDialog';
import RenameDialog from '../RenameDialog';
import ConfirmDialog from '../ConfirmDialog';
import MergeAnnotatedFloorplanModal from '../MergeAnnotatedFloorplanModal';
import { useAdmin } from '../../hooks/useAdmin';
import { Heading } from '../ui';
import '../../styles/ProjectDetail.css';
import {
  REPROCESS_VARIANTS,
  reprocessPayload,
} from '../../utils/reprocessActions';
import {
  reprocessSuccessNotice,
  PROCESS_STATUS_ACTION_LABEL,
  markDraftNoticeSeen,
} from '../../constants/depthFinalization';

/**
 * A design project's Plans (moved out of ProjectDetail for the books desk's
 * phase 3b, claude-docs/2026-09-26-books-desk-phase3b-plan.md): its scans and
 * rooms, every menu on them (rename, delete, reprocess, export), the floor-plan
 * upload, the renderings, and the dialogs those open. Used by ProjectDetail
 * and by the contractor's job page, which each own the project's loading
 * (`useProjectDesign`) and hand it in.
 *
 * onReload  re-read the project after a change (the host's `reload`)
 * notify    show a notification bubble the host owns: ({ message, type })
 */

// Renders an admin-flagged menu item label: "<text> Admin*" pill.
// Kept local — only used inside ProjectDetail's scan-card dropdowns.
function AdminMenuItemLabel({ text }) {
  return (
    <span className="admin-menu-item__label">
      <span>{text}</span>
      <span className="admin-menu-item__pill">
        <svg
          width="10"
          height="10"
          viewBox="0 0 24 24"
          fill="currentColor"
          className="admin-menu-item__pill-icon"
          aria-hidden="true"
        >
          <path d="M12 2L14.09 8.26L20.79 9.15L16.4 13.41L17.45 20.09L12 16.77L6.55 20.09L7.6 13.41L3.21 9.15L9.91 8.26L12 2Z" />
        </svg>
        Admin
      </span>
    </span>
  );
}

export default function ProjectPlansTab({
  projectId,
  project,
  structures,
  roomCounts,
  canEditProject,
  onReload,
  notify,
}) {
  const id = projectId;
  const navigate = useNavigate();
  const { showLoading, hideLoading } = useLoading();
  const { isAdmin } = useAdmin();
  // one list: the page used to keep the same query twice under two names
  const preTransformedStructures = structures;
  const [preTransformedRooms, setPreTransformedRooms] = useState([]);
  // Room DOCUMENTS are only fetched when the Rooms view is opened.
  const [roomsLoaded, setRoomsLoaded] = useState(false);
  const [roomsLoading, setRoomsLoading] = useState(false);
  const [showIOSDialog, setShowIOSDialog] = useState(false);
  const [scanViewMode, setScanViewMode] = useState('scans'); // 'scans' or 'rooms'
  const [showPreTransformedViewer, setShowPreTransformedViewer] =
    useState(false);
  const [selectedStructureId, setSelectedStructureId] = useState(null);
  const [selectedRoomId, setSelectedRoomId] = useState(null);
  const [reprocessingStructures, setReprocessingStructures] = useState(
    new Set()
  );
  const [ifcExportDialog, setIfcExportDialog] = useState({
    isOpen: false,
    structureId: null,
    structureName: null,
  });
  const [dxfExportDialog, setDxfExportDialog] = useState({
    isOpen: false,
    structureId: null,
    structureName: null,
  });
  const [scanRenameDialog, setScanRenameDialog] = useState({
    isOpen: false,
    scanId: null,
    scanName: null,
  });
  const [roomRenameDialog, setRoomRenameDialog] = useState({
    isOpen: false,
    roomId: null,
    roomName: null,
  });
  const [menuOpenScanId, setMenuOpenScanId] = useState(null);
  const [floorplanUploadStatus, setFloorplanUploadStatus] = useState(null);
  const [planChoice, setPlanChoice] = useState(null);
  const [showMergeModal, setShowMergeModal] = useState(false);
  const floorplanInputRef = useRef(null);
  const [menuOpenRoomId, setMenuOpenRoomId] = useState(null);
  const [scanDeleteDialog, setScanDeleteDialog] = useState({
    isOpen: false,
    scanId: null,
    scanName: null,
    roomCount: 0,
    elementCount: 0,
    isOwner: true,
  });

  // Handle floor plan image upload (admin only)
  const handleFloorplanUpload = useCallback(
    async (e) => {
      const file = e.target.files?.[0];
      if (!file) return;
      // Reset input so same file can be re-selected
      e.target.value = '';
      try {
        setFloorplanUploadStatus('Uploading...');
        const result = await uploadAndExtractFloorplan(
          file,
          id,
          (stage, msg) => setFloorplanUploadStatus(msg),
          {
            // A DXF can hold several floor plans; ask rather than import
            // one storey and silently drop the rest.
            onChoosePlan: (plans) =>
              new Promise((resolve) => setPlanChoice({ plans, resolve })),
          }
        );
        setFloorplanUploadStatus(null);
        // Navigate to the new structure
        navigate(`/structure/${result.structureId}`);
      } catch (err) {
        console.error('Floor plan upload failed:', err);
        // Sticky: extraction can fail minutes after the click, so a timed-out
        // status would vanish unseen. Cleared on the next upload attempt.
        setFloorplanUploadStatus(`Error: ${err.message}`);
      }
    },
    [id, navigate]
  );

  // Fetch room DOCUMENTS only when the Rooms view is opened, once per project.
  // The Scans view (the default) needs counts, not rooms, and those come from
  // an aggregation — so opening this project downloads no room geometry.
  //
  // The in-flight guard is a REF, not state, and `roomsLoading` is deliberately
  // NOT a dependency. As state in the dep array it self-cancelled: setting it
  // re-ran the effect, whose cleanup flipped the first run's `cancelled` flag,
  // so the resolved fetch committed nothing and the re-run bailed on the very
  // flag it had just set — the view sat on "Loading rooms…" forever.
  const roomsFetchStartedRef = useRef(false);
  useEffect(() => {
    if (scanViewMode !== 'rooms' || roomsFetchStartedRef.current) return;
    if (preTransformedStructures.length === 0) return;

    roomsFetchStartedRef.current = true;
    setRoomsLoading(true);
    getPreTransformedRoomsForMultipleStructures(
      preTransformedStructures.map((st) => st.id)
    )
      .then((fetched) => {
        setPreTransformedRooms(fetched);
        setRoomsLoaded(true);
      })
      .catch((err) => {
        // Allow a retry on the next switch into the view.
        roomsFetchStartedRef.current = false;
        console.error('Error loading rooms for the Rooms view:', err);
      })
      .finally(() => setRoomsLoading(false));
  }, [scanViewMode, preTransformedStructures]);

  // Navigating to a different project must re-arm the lazy fetch.
  useEffect(() => {
    roomsFetchStartedRef.current = false;
    setRoomsLoaded(false);
    setPreTransformedRooms([]);
  }, [id]);

  const handleStructureClick = (structure, event) => {
    // Don't navigate if clicking on menu button
    if (
      event?.target?.closest('.structure-menu-button') ||
      event?.target?.closest('.structure-menu-dropdown')
    ) {
      return;
    }
    // Open the unified structure viewer on its in-place floorplan tab.
    const params = new URLSearchParams();
    if (id) params.append('projectId', id);
    params.append('navigationMode', 'floorplan');
    navigate(`/structure/${structure.id}?${params.toString()}`);
  };

  const handleRoomClick = (room, event) => {
    // Don't navigate if clicking on menu button
    if (
      event?.target?.closest('.room-menu-button') ||
      event?.target?.closest('.room-menu-dropdown')
    ) {
      return;
    }
    // Navigate to the structure page with room ID for both data sources
    // Include projectId in the URL parameters
    const structureId = room.structureId;
    const params = new URLSearchParams();
    if (id) params.append('projectId', id);
    params.append('roomId', room.id);
    const queryString = params.toString();
    navigate(`/structure/${structureId}?${queryString}`);
  };

  // Helper function to check if a room belongs to a structure
  const roomBelongsToStructure = (room, structure) => {
    const roomStructureId = room.structureId;
    const structureId = structure.id;
    return roomStructureId === structureId;
  };

  // Get structure name for a room
  const getStructureNameForRoom = (room) => {
    const structuresList = preTransformedStructures;
    const structure = structuresList.find((s) =>
      roomBelongsToStructure(room, s)
    );
    const scanName =
      structure?.name || structure?.structureName || 'Unnamed Scan';
    return `Scan: ${scanName}`;
  };

  // Scan menu handlers
  const handleScanMenuToggle = (scanId, event) => {
    event.stopPropagation();
    setMenuOpenScanId(menuOpenScanId === scanId ? null : scanId);
  };

  const handleScanRename = (scan, event) => {
    event.stopPropagation();
    setMenuOpenScanId(null);
    setScanRenameDialog({
      isOpen: true,
      scanId: scan.id,
      scanName: scan.name || scan.structureName || 'Unnamed Scan',
    });
  };

  const handleConfirmScanRename = async (newName) => {
    const { scanId } = scanRenameDialog;

    try {
      showLoading();
      // Update structure name in Firebase
      const { updateStructure } = await import(
        '../../services/structureService'
      );
      await updateStructure(scanId, { name: newName });

      // Reload project data to reflect changes
      await onReload();

      setScanRenameDialog({
        isOpen: false,
        scanId: null,
        scanName: null,
      });

      notify({
        message: 'Scan renamed successfully!',
        type: 'success',
      });
    } catch (error) {
      console.error('Error renaming scan:', error);
      notify({
        message: 'Failed to rename scan. Please try again.',
        type: 'error',
      });
    } finally {
      hideLoading();
    }
  };

  // Room menu handlers
  const handleRoomMenuToggle = (roomId, event) => {
    event.stopPropagation();
    setMenuOpenRoomId(menuOpenRoomId === roomId ? null : roomId);
  };

  const handleRoomRename = (room, event) => {
    event.stopPropagation();
    setMenuOpenRoomId(null);
    setRoomRenameDialog({
      isOpen: true,
      roomId: room.id,
      // the stored name, never the card's title: saving the box renames the
      // room
      roomName: roomNameEditSeed(room),
    });
  };

  const handleConfirmRoomRename = async (newName) => {
    const { roomId } = roomRenameDialog;

    try {
      showLoading();
      // Update room name in Firebase
      const { updateRoom } = await import('../../services/structureService');
      await updateRoom(roomId, { name: newName });

      // Reload project data to reflect changes
      await onReload();

      setRoomRenameDialog({
        isOpen: false,
        roomId: null,
        roomName: null,
      });

      notify({
        message: 'Room renamed successfully!',
        type: 'success',
      });
    } catch (error) {
      console.error('Error renaming room:', error);
      notify({
        message: 'Failed to rename room. Please try again.',
        type: 'error',
      });
    } finally {
      hideLoading();
    }
  };

  const handleScanDelete = async (scan, event) => {
    event.stopPropagation();

    // Whoever may change the project's scans may delete one — the owner or an
    // owner / office member of its organization, which is what the rules for
    // /structures and /originalScans admit. The scan's own createdBy is not
    // the gate: an office member deleting a scan the founder uploaded is the
    // normal case, and a copied project's scans carry somebody else's uid.
    const isOwner = canEditProject;

    if (!isOwner) {
      // User may not change this project's scans, show error dialog
      setScanDeleteDialog({
        isOpen: true,
        scanId: scan.id,
        scanName: scan.name || scan.structureName || 'Unnamed Scan',
        roomCount: 0,
        elementCount: 0,
        isOwner: false,
      });
      return;
    }

    // Count rooms and elements for this scan
    let roomCount = 0;
    let elementCount = 0;

    // Room count from the aggregation, which is loaded for every scan on
    // page load. Room DOCUMENTS are not, so filtering `preTransformedRooms`
    // here would report 0 rooms on a scan the user is about to delete unless
    // they had happened to open the Rooms view first.
    const countedRooms = roomCounts[scan.id];
    roomCount =
      typeof countedRooms === 'number'
        ? countedRooms
        : preTransformedRooms.filter((room) => room.structureId === scan.id)
            .length;

    // Count elements (walls, floors, objects) in the structure
    if (scan.walls) elementCount += scan.walls.length;
    if (scan.floors) elementCount += scan.floors.length;
    if (scan.objects) elementCount += scan.objects.length;

    setScanDeleteDialog({
      isOpen: true,
      scanId: scan.id,
      scanName: scan.name || scan.structureName || 'Unnamed Scan',
      roomCount,
      elementCount,
      isOwner: true,
    });
  };

  const handleConfirmScanDelete = async () => {
    const { scanId } = scanDeleteDialog;

    try {
      showLoading();

      // Import the delete function
      const { deleteStructure } = await import(
        '../../services/structureService'
      );

      // Delete the structure
      await deleteStructure(scanId);

      // Reload project data to update the UI
      await onReload();

      // Close dialog
      setScanDeleteDialog({
        isOpen: false,
        scanId: null,
        scanName: null,
        roomCount: 0,
        elementCount: 0,
        isOwner: true,
      });

      // Show success notification
      notify({
        message: 'Scan deleted successfully',
        type: 'success',
      });
    } catch (error) {
      console.error('Error deleting scan:', error);
      notify({
        message: 'Failed to delete scan. Please try again.',
        type: 'error',
      });
    } finally {
      hideLoading();
    }
  };

  const handleCancelScanDelete = () => {
    setScanDeleteDialog({
      isOpen: false,
      scanId: null,
      scanName: null,
      roomCount: 0,
      elementCount: 0,
      isOwner: true,
    });
  };

  // Close menus when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        !event.target.closest('.structure-menu-button') &&
        !event.target.closest('.structure-menu-dropdown')
      ) {
        setMenuOpenScanId(null);
      }
      if (
        !event.target.closest('.room-menu-button') &&
        !event.target.closest('.room-menu-dropdown')
      ) {
        setMenuOpenRoomId(null);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  const handleReprocessScan = async (
    scanId,
    variant = REPROCESS_VARIANTS.standard
  ) => {
    const runWallAlignment = variant.runWallAlignment;
    try {
      // Spinner + disabled state on this scan's kebab row.
      setReprocessingStructures((prev) => new Set(prev).add(scanId));
      showLoading();

      // Show which environment we're using
      const usingEmulator = process.env.NODE_ENV === 'development';
      console.log('🔍 Reprocess environment:', {
        NODE_ENV: process.env.NODE_ENV,
        usingEmulator,
      });

      if (usingEmulator) {
        console.log('🔧 Should be using Functions emulator for reprocessing');
      }

      // Call the cloud function to reprocess the scan
      const processStructure = createCallable('processStructure');
      console.log(
        '📞 Calling processStructure with scanId:',
        scanId,
        'runWallAlignment:',
        runWallAlignment
      );
      // One builder, shared with both viewer menus — see `reprocessPayload`.
      const result = await processStructure(reprocessPayload(scanId, variant));

      console.log('Reprocess result:', result);

      // Reload the project data to show updated structures
      await onReload();

      // Show success notification
      const successMessage = usingEmulator
        ? 'Scan reprocessed successfully! (via emulator)'
        : 'Scan reprocessed successfully!';
      // One message, not two — a run entering the refinement cycle carries the
      // draft caveat here rather than as a second notice moments later. Built
      // from what the server decided about the cycle, not from the button.
      const notice = reprocessSuccessNotice(successMessage, {
        requested: variant.depthRefinement,
        decision: result?.data?.depthFinalization,
      });
      notify({
        message: notice.message,
        type: 'success',
        // Waits to be dismissed — see FloorplanView.
        duration: null,
        // The caveat dates the refined version from when the UPLOADS finish,
        // which only the panel can answer — and this toast suppresses the
        // under-card notice that would otherwise carry the link. There is no
        // viewer on this page, so the action navigates to the scan with the
        // panel requested rather than opening one here.
        ...(notice.linkToStatus
          ? {
              actionLabel: PROCESS_STATUS_ACTION_LABEL,
              onAction: () =>
                navigate(
                  processStatusUrl({ structureId: scanId, projectId: id })
                ),
            }
          : {}),
      });
      // The caveat has now been delivered; don't repeat it under the card.
      if (notice.deliveredDraftNotice) markDraftNoticeSeen(scanId);
    } catch (error) {
      console.error('Error reprocessing scan:', error);
      notify({
        message: `Failed to reprocess scan: ${error.message}`,
        type: 'error',
      });
    } finally {
      setReprocessingStructures((prev) => {
        const next = new Set(prev);
        next.delete(scanId);
        return next;
      });
      hideLoading();
    }
  };

  const handleMigrateStructure = async (structureId) => {
    try {
      // Show loading screen
      showLoading();

      console.log('🚀 Starting migration for structure:', structureId);

      // Find the structure to get the jsonURL
      const structure = structures.find((s) => s.id === structureId);
      if (!structure) {
        throw new Error('Structure not found');
      }

      if (!structure.jsonURL) {
        throw new Error('No JSON URL found for this structure');
      }

      console.log('📋 Structure details:', {
        id: structure.id,
        jsonURL: structure.jsonURL,
        name: structure.name || structure.structureName,
      });

      // Show which environment we're using
      const usingEmulator = process.env.NODE_ENV === 'development';
      console.log('🔍 Migration environment:', {
        NODE_ENV: process.env.NODE_ENV,
        usingEmulator,
      });

      if (usingEmulator) {
        console.log('🔧 Should be using Functions emulator for migration');
      }

      // Use the existing processStructure cloud function with scanId and jsonURL
      const processStructure = createCallable('processStructure');
      console.log('📞 Calling processStructure with scanId and jsonURL:', {
        scanId: structureId,
        jsonURL: structure.jsonURL,
      });

      const result = await processStructure({
        scanId: structureId,
        jsonURL: structure.jsonURL,
      });

      console.log('Migration result:', result);

      // Reload the project data to show updated structures
      await onReload();

      // Show success notification
      notify({
        message: usingEmulator
          ? 'Structure migrated to pre-transformed format! (via emulator)'
          : 'Structure migrated to pre-transformed format!',
        type: 'success',
      });
    } catch (error) {
      console.error('Error migrating structure:', error);
      notify({
        message: `Failed to migrate structure: ${error.message}`,
        type: 'error',
      });
    } finally {
      // Hide loading screen
      hideLoading();
    }
  };

  const handleExportToJSON = async (structureId, e) => {
    e.stopPropagation(); // Prevent card click

    try {
      // Show loading state
      showLoading();

      // Find the structure to get its name
      const structure = [...structures, ...preTransformedStructures].find(
        (s) => s.id === structureId
      );
      const structureName =
        structure?.name || structure?.structureName || 'Structure';

      // Export to JSON with no transformations (always from rooms/structures collections)
      console.log('Exporting structure:', structureId);
      const jsonData = await exportStructureToJSON(structureId);

      // Create blob for download
      const blob = new Blob([jsonData], { type: 'application/json' });

      // Create download link
      const url = URL.createObjectURL(blob);

      // Trigger download using structureId for filename
      const a = document.createElement('a');
      a.href = url;
      a.download = `${structureName}_export.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);

      // Clean up
      setTimeout(() => URL.revokeObjectURL(url), 100);

      // Show success notification
      notify({
        message: 'JSON export complete! The file has been downloaded.',
        type: 'success',
      });
    } catch (error) {
      console.error('Export failed:', error);
      notify({
        message: 'Failed to export to JSON. Please try again.',
        type: 'error',
      });
    } finally {
      hideLoading();
    }
  };

  const handleExportToIFC = async (structureId, e) => {
    e.stopPropagation(); // Prevent card click

    // Find the structure to get its name
    const structure = [...structures, ...preTransformedStructures].find(
      (s) => s.id === structureId
    );

    // Show the export dialog
    setIfcExportDialog({
      isOpen: true,
      structureId,
      structureName: structure?.name || 'Structure',
    });
  };

  const handleIFCExportConfirm = async (source) => {
    const { structureId, structureName } = ifcExportDialog;

    // Close dialog
    setIfcExportDialog({
      isOpen: false,
      structureId: null,
      structureName: null,
    });

    // Small delay to let dialog close animation complete
    await new Promise((resolve) => setTimeout(resolve, 100));

    try {
      // Show loading state
      showLoading();

      // Convert using Cloud Function with selected source
      console.log(
        'Converting structure to IFC:',
        structureId,
        'Source:',
        source
      );
      const result = await convertToIFCCloud(structureId, source);

      // Save metadata to Firestore
      console.log('Saving IFC conversion metadata...');
      await saveIFCConversion(structureId, result);

      // Download the file properly using fetch to handle CORS
      console.log('Downloading IFC file:', result.ifcUrl);

      try {
        // Fetch the file
        const response = await fetch(result.ifcUrl);
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        // Get the blob
        const blob = await response.blob();

        // Create a blob URL
        const blobUrl = URL.createObjectURL(blob);

        // Create a temporary link and click it
        const a = document.createElement('a');
        a.href = blobUrl;
        a.download = `${structureName || structureId}.ifc`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);

        // Clean up the blob URL
        setTimeout(() => URL.revokeObjectURL(blobUrl), 100);

        // Show success message
        notify({
          message: 'IFC export complete! The file has been downloaded.',
          type: 'success',
        });
      } catch (downloadError) {
        console.error('Download failed:', downloadError);
        // Fallback: open in new tab
        console.log('Falling back to opening in new tab');
        window.open(result.ifcUrl, '_blank');
        notify({
          message:
            'IFC export complete! The file should open in a new tab. Use "Save As" to download.',
          type: 'success',
        });
      }

      // Log conversion report if available
      if (result.reportUrl) {
        console.log('IFC Conversion Report:', result.reportUrl);
      }
    } catch (error) {
      console.error('IFC export error:', error);
      console.error('Error details:', error.message, error.stack);
      notify({
        message: `Failed to export to IFC: ${error.message || 'Unknown error'}`,
        type: 'error',
      });
    } finally {
      console.log('Hiding loading screen for IFC export');
      hideLoading();
    }
  };

  const handleExportToDXF = async (structureId, e) => {
    e.stopPropagation(); // Prevent card click

    // Find the structure to get its name
    const structure = [...structures, ...preTransformedStructures].find(
      (s) => s.id === structureId
    );

    // Show the export dialog
    setDxfExportDialog({
      isOpen: true,
      structureId,
      structureName: structure?.name || 'Structure',
    });
  };

  // DXFExportDialog emits one `{ mode, source }` object (see its onExport).
  const handleDXFExportConfirm = async ({ mode, source }) => {
    const { structureId, structureName } = dxfExportDialog;

    // Close dialog
    setDxfExportDialog({
      isOpen: false,
      structureId: null,
      structureName: null,
    });

    try {
      // Show loading state
      showLoading();

      // Convert using Cloud Function with selected source and mode
      console.log(
        'Converting structure to DXF:',
        structureId,
        'Source:',
        source,
        'Mode:',
        mode
      );

      const result = await convertToDXFCloud(structureId, mode, source);

      // Download the file
      console.log('Downloading DXF file:', result.dxfUrl);

      try {
        // Fetch the file
        const response = await fetch(result.dxfUrl);
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        // Get the blob
        const blob = await response.blob();

        // Create a blob URL
        const blobUrl = URL.createObjectURL(blob);

        // Create a temporary link and click it
        const a = document.createElement('a');
        a.href = blobUrl;
        a.download = `${structureName}_${mode}.dxf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);

        // Clean up the blob URL
        setTimeout(() => URL.revokeObjectURL(blobUrl), 100);

        // Show success message
        notify({
          message: `DXF export complete! The ${mode.toUpperCase()} file has been downloaded.`,
          type: 'success',
        });
      } catch (downloadError) {
        console.error('Download failed:', downloadError);
        // Fallback: open in new tab
        console.log('Falling back to opening in new tab');
        window.open(result.dxfUrl, '_blank');
        notify({
          message: `DXF export complete! The ${mode.toUpperCase()} file should open in a new tab. Use "Save As" to download.`,
          type: 'success',
        });
      }
    } catch (error) {
      console.error('DXF export error:', error);
      notify({
        message: 'Failed to export to DXF. Please try again.',
        type: 'error',
      });
    } finally {
      hideLoading();
    }
  };

  const formatDate = formatStructureDate;

  return (
    <>
      {project.orgId && canEditProject && (
        <ProjectAssignments project={project} projectId={id} />
      )}
      <section className="content-section">
        <div className="section-header">
          <Heading level={3} tone="dark">
            Plans
          </Heading>
          <div className="scan-filter-controls">
            <div className="scan-toggle" role="group" aria-label="View mode">
              <span className="scan-toggle__label">View:</span>
              <button
                type="button"
                className={`scan-toggle__btn ${scanViewMode === 'scans' ? 'is-active' : ''}`}
                onClick={() => setScanViewMode('scans')}
                aria-pressed={scanViewMode === 'scans'}
              >
                Scans
              </button>
              <button
                type="button"
                className={`scan-toggle__btn ${scanViewMode === 'rooms' ? 'is-active' : ''}`}
                onClick={() => setScanViewMode('rooms')}
                aria-pressed={scanViewMode === 'rooms'}
              >
                Rooms
              </button>
            </div>
          </div>
        </div>

        {(() => {
          // Show every structure in the project — including source
          // scans that have been folded into a merged result.
          // Progressive merging isn't supported, so to re-register
          // the user has to pick the originals; hiding them would
          // make that impossible.
          const displayStructures = preTransformedStructures;
          const visibleStructureIds = new Set(
            displayStructures.map((s) => s.id)
          );
          const displayRooms = preTransformedRooms.filter(
            (r) => !r.structureId || visibleStructureIds.has(r.structureId)
          );

          // Counts stand in for the room list here: room documents
          // are not loaded until the Rooms view is opened, so
          // `displayRooms` is legitimately empty on a project that
          // does have rooms.
          const totalCountedRooms = Object.values(roomCounts).reduce(
            (sum, n) => sum + (typeof n === 'number' ? n : 0),
            0
          );
          if (
            displayStructures.length === 0 &&
            displayRooms.length === 0 &&
            totalCountedRooms === 0
          ) {
            return (
              <>
                <div className="documents-placeholder">
                  <p>No rooms scanned yet</p>
                  <p className="documents-hint">
                    Room scans will appear here after processing
                  </p>
                </div>
                <div className="structures-grid structures-grid--below-empty">
                  {/* Add New Room Scan Box - only for those who can edit */}
                  {canEditProject && (
                    <div
                      className="structure-card new-room-scan-card"
                      onClick={() => setShowIOSDialog(true)}
                    >
                      <div className="structure-card-header">
                        <h3>Add New Room Scan</h3>
                      </div>
                      <div className="structure-thumbnail-placeholder">
                        <svg
                          viewBox="0 0 24 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M12 5V19M5 12H19"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </div>
                      <div className="structure-meta-info">
                        <p className="structure-date">Use iOS app to scan</p>
                      </div>
                    </div>
                  )}

                  {/* Upload Floor Plan - admin only */}
                  {isAdmin && (
                    <div
                      className="structure-card new-room-scan-card"
                      onClick={() => floorplanInputRef.current?.click()}
                    >
                      <div className="structure-card-header">
                        <h3>Upload Floor Plan</h3>
                      </div>
                      <div className="structure-thumbnail-placeholder">
                        <svg
                          viewBox="0 0 24 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M4 16L4 17C4 18.6569 5.34315 20 7 20L17 20C18.6569 20 20 18.6569 20 17L20 16M16 8L12 4M12 4L8 8M12 4L12 16"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </div>
                      <div className="structure-meta-info">
                        <p className="structure-date">
                          {floorplanUploadStatus || 'Extract from image'}
                        </p>
                      </div>
                      <input
                        ref={floorplanInputRef}
                        type="file"
                        accept="image/*,.dxf"
                        className="visually-hidden-file"
                        onChange={handleFloorplanUpload}
                      />
                    </div>
                  )}

                  {/* Upload paired floor plans - admin only */}
                  {isAdmin && (
                    <div
                      className="structure-card new-room-scan-card"
                      onClick={() => setShowMergeModal(true)}
                    >
                      <div className="structure-card-header">
                        <h3>Upload paired floor plans</h3>
                      </div>
                      <div className="structure-thumbnail-placeholder">
                        <svg
                          viewBox="0 0 24 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M4 7h7M4 12h7M4 17h7M20 7h-5M20 12h-5M20 17h-5M14 4v16"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </div>
                      <div className="structure-meta-info">
                        <p className="structure-date">
                          Upload plan + dimensions pair
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </>
            );
          }

          return (
            <div className="structures-grid">
              {scanViewMode === 'scans' ? (
                // Show structures (scans)
                displayStructures.map((structure) => {
                  // Room count comes from the server-side count
                  // aggregation, so the Scans view never needs the
                  // room documents. `null` means the count failed
                  // for this scan alone; fall back to whatever rooms
                  // happen to be loaded (the Rooms view fills them).
                  const counted = roomCounts[structure.id];
                  const roomCount =
                    typeof counted === 'number'
                      ? counted
                      : displayRooms.filter((room) =>
                          roomBelongsToStructure(room, structure)
                        ).length;
                  const isMerged = structure.scanSource === 'merged';
                  const sourceIds = Array.isArray(structure.sourceScanIds)
                    ? structure.sourceScanIds
                    : [];
                  // `sourceScanNames` is a snapshot persisted at
                  // merge time. Fall back to IDs for older
                  // merged docs that predate the field.
                  const persistedNames = Array.isArray(
                    structure.sourceScanNames
                  )
                    ? structure.sourceScanNames
                    : [];
                  const sourceNames = sourceIds.map(
                    (id, i) => persistedNames[i] || id
                  );

                  return (
                    <div
                      key={structure.id}
                      className="structure-card"
                      onClick={(e) => handleStructureClick(structure, e)}
                    >
                      {canEditProject && (
                        <div className="structure-menu-container">
                          <button
                            className="structure-menu-button"
                            onClick={(e) =>
                              handleScanMenuToggle(structure.id, e)
                            }
                            aria-label="Structure menu"
                          >
                            <svg
                              width="20"
                              height="20"
                              viewBox="0 0 24 24"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <circle
                                cx="12"
                                cy="5"
                                r="1.5"
                                fill="currentColor"
                              />
                              <circle
                                cx="12"
                                cy="12"
                                r="1.5"
                                fill="currentColor"
                              />
                              <circle
                                cx="12"
                                cy="19"
                                r="1.5"
                                fill="currentColor"
                              />
                            </svg>
                          </button>
                          {menuOpenScanId === structure.id && (
                            <div className="structure-menu-dropdown">
                              <button
                                className="menu-item"
                                onClick={(e) => handleScanRename(structure, e)}
                              >
                                <svg
                                  width="16"
                                  height="16"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  xmlns="http://www.w3.org/2000/svg"
                                >
                                  <path
                                    d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  />
                                </svg>
                                Rename
                              </button>
                              <button
                                className="menu-item"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setMenuOpenScanId(null);
                                  setSelectedStructureId(structure.id);
                                  setSelectedRoomId(null);
                                  setShowPreTransformedViewer(true);
                                }}
                              >
                                <svg
                                  width="16"
                                  height="16"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  xmlns="http://www.w3.org/2000/svg"
                                >
                                  <path
                                    d="M12 2L2 7L12 12L22 7L12 2Z"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  />
                                  <path
                                    d="M2 17L12 22L22 17"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  />
                                  <path
                                    d="M2 12L12 17L22 12"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  />
                                </svg>
                                3D Debug
                              </button>

                              {isAdmin && (
                                <button
                                  className="menu-item admin-menu-item"
                                  onClick={(e) => {
                                    setMenuOpenScanId(null);
                                    handleExportToJSON(structure.id, e);
                                  }}
                                >
                                  <svg
                                    width="16"
                                    height="16"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                  >
                                    <path
                                      d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z"
                                      stroke="currentColor"
                                      strokeWidth="2"
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                    />
                                    <path
                                      d="M14 2V8H20"
                                      stroke="currentColor"
                                      strokeWidth="2"
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                    />
                                    <path
                                      d="M12 11V17M9 14L12 17L15 14"
                                      stroke="currentColor"
                                      strokeWidth="2"
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                    />
                                  </svg>
                                  <AdminMenuItemLabel text="Export JSON" />
                                </button>
                              )}
                              <button
                                className="menu-item"
                                onClick={(e) => {
                                  setMenuOpenScanId(null);
                                  handleExportToIFC(structure.id, e);
                                }}
                              >
                                <svg
                                  width="16"
                                  height="16"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  xmlns="http://www.w3.org/2000/svg"
                                >
                                  <path
                                    d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  />
                                  <path
                                    d="M14 2V8H20"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  />
                                  <text
                                    x="12"
                                    y="16"
                                    fontSize="6"
                                    textAnchor="middle"
                                    fill="currentColor"
                                  >
                                    IFC
                                  </text>
                                </svg>
                                Export IFC
                              </button>
                              <button
                                className="menu-item"
                                onClick={(e) => {
                                  setMenuOpenScanId(null);
                                  handleExportToDXF(structure.id, e);
                                }}
                              >
                                <svg
                                  width="16"
                                  height="16"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  xmlns="http://www.w3.org/2000/svg"
                                >
                                  <path
                                    d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  />
                                  <path
                                    d="M14 2V8H20"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  />
                                  <text
                                    x="12"
                                    y="16"
                                    fontSize="6"
                                    textAnchor="middle"
                                    fill="currentColor"
                                  >
                                    DXF
                                  </text>
                                </svg>
                                Export DXF
                              </button>
                              {isAdmin && (
                                <button
                                  className="menu-item admin-menu-item"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setMenuOpenScanId(null);
                                    handleReprocessScan(
                                      structure.id,
                                      REPROCESS_VARIANTS.standard
                                    );
                                  }}
                                  disabled={reprocessingStructures.has(
                                    structure.id
                                  )}
                                >
                                  {reprocessingStructures.has(structure.id) ? (
                                    <svg
                                      className="spinner"
                                      width="16"
                                      height="16"
                                      viewBox="0 0 24 24"
                                      fill="none"
                                      xmlns="http://www.w3.org/2000/svg"
                                    >
                                      <path
                                        d="M12 2V6M12 18V22M4.93 4.93L7.76 7.76M16.24 16.24L19.07 19.07M2 12H6M18 12H22M4.93 19.07L7.76 16.24M16.24 7.76L19.07 4.93"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                      />
                                    </svg>
                                  ) : (
                                    <svg
                                      width="16"
                                      height="16"
                                      viewBox="0 0 24 24"
                                      fill="none"
                                      xmlns="http://www.w3.org/2000/svg"
                                    >
                                      <path
                                        d="M23 4V10H17"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                      />
                                      <path
                                        d="M1 20V14H7"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                      />
                                      <path
                                        d="M3.51 9C3.8 5.5 6.94 2.7 10.67 2.06C15.67 1.2 20.24 4.48 20.92 9.29L23 7"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                      />
                                      <path
                                        d="M20.49 15C20.2 18.5 17.06 21.3 13.33 21.94C8.33 22.8 3.76 19.52 3.08 14.71L1 17"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                      />
                                    </svg>
                                  )}
                                  <AdminMenuItemLabel
                                    text={REPROCESS_VARIANTS.standard.label}
                                  />
                                </button>
                              )}
                              {isAdmin && (
                                <button
                                  className="menu-item admin-menu-item"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setMenuOpenScanId(null);
                                    handleReprocessScan(
                                      structure.id,
                                      REPROCESS_VARIANTS.align
                                    );
                                  }}
                                  disabled={reprocessingStructures.has(
                                    structure.id
                                  )}
                                >
                                  {reprocessingStructures.has(structure.id) ? (
                                    <svg
                                      className="spinner"
                                      width="16"
                                      height="16"
                                      viewBox="0 0 24 24"
                                    >
                                      <circle
                                        cx="12"
                                        cy="12"
                                        r="10"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        fill="none"
                                        strokeDasharray="31.4 31.4"
                                        transform="rotate(-90 12 12)"
                                      >
                                        <animateTransform
                                          attributeName="transform"
                                          type="rotate"
                                          from="0 12 12"
                                          to="360 12 12"
                                          dur="1s"
                                          repeatCount="indefinite"
                                        />
                                      </circle>
                                    </svg>
                                  ) : (
                                    <svg
                                      width="16"
                                      height="16"
                                      viewBox="0 0 24 24"
                                      fill="none"
                                      xmlns="http://www.w3.org/2000/svg"
                                    >
                                      <path
                                        d="M23 4V10H17"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                      />
                                      <path
                                        d="M1 20V14H7"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                      />
                                      <path
                                        d="M3.51 9C3.8 5.5 6.94 2.7 10.67 2.06C15.67 1.2 20.24 4.48 20.92 9.29L23 7"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                      />
                                      <path
                                        d="M20.49 15C20.2 18.5 17.06 21.3 13.33 21.94C8.33 22.8 3.76 19.52 3.08 14.71L1 17"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                      />
                                      <path
                                        d="M9 11L12 14L15 11"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                      />
                                    </svg>
                                  )}
                                  <AdminMenuItemLabel
                                    text={REPROCESS_VARIANTS.align.label}
                                  />
                                </button>
                              )}
                              {/* Show Migrate option only for admin users and structures from migration collections */}
                              {isAdmin &&
                                structures.find(
                                  (s) => s.id === structure.id
                                ) && (
                                  <button
                                    className="menu-item admin-menu-item"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setMenuOpenScanId(null);
                                      handleMigrateStructure(structure.id);
                                    }}
                                  >
                                    <svg
                                      width="16"
                                      height="16"
                                      viewBox="0 0 24 24"
                                      fill="none"
                                      xmlns="http://www.w3.org/2000/svg"
                                    >
                                      <path
                                        d="M7 7h10v3l4-4-4-4v3H5v6h2V7z"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                      />
                                      <path
                                        d="M17 17H7v-3l-4 4 4 4v-3h12v-6h-2v4z"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                      />
                                    </svg>
                                    <AdminMenuItemLabel text="Migrate" />
                                  </button>
                                )}
                              <button
                                className="menu-item delete-item"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setMenuOpenScanId(null);
                                  handleScanDelete(structure, e);
                                }}
                              >
                                <svg
                                  width="16"
                                  height="16"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  xmlns="http://www.w3.org/2000/svg"
                                >
                                  <path
                                    d="M3 6h18M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2m3 0v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6h14zM10 11v6M14 11v6"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  />
                                </svg>
                                Delete
                              </button>
                            </div>
                          )}
                        </div>
                      )}
                      <div className="structure-card-header">
                        <h3>
                          {structure.name ||
                            structure.structureName ||
                            'Unnamed Scan'}
                        </h3>
                      </div>
                      {structure.thumbnailUrl ? (
                        <img
                          src={structure.thumbnailUrl}
                          alt={structure.structureName || 'Structure thumbnail'}
                          className="structure-thumbnail"
                        />
                      ) : (
                        <div className="structure-thumbnail-placeholder">
                          <svg
                            viewBox="0 0 24 24"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <rect
                              x="3"
                              y="3"
                              width="18"
                              height="18"
                              rx="2"
                              stroke="currentColor"
                              strokeWidth="2"
                            />
                            <path
                              d="M3 9H21"
                              stroke="currentColor"
                              strokeWidth="2"
                            />
                            <path
                              d="M9 3V21"
                              stroke="currentColor"
                              strokeWidth="2"
                            />
                          </svg>
                        </div>
                      )}
                      <div className="structure-meta-info">
                        <p className="structure-room-count">
                          {roomCount} {roomCount === 1 ? 'room' : 'rooms'}
                        </p>
                        {isMerged && sourceNames.length > 0 && (
                          <p
                            className="structure-merged-from"
                            title={sourceNames.join('\n')}
                          >
                            Merged from {sourceNames.length} scan
                            {sourceNames.length === 1 ? '' : 's'}:{' '}
                            {sourceNames.join(', ')}
                          </p>
                        )}
                        <p className="structure-date">
                          {formatDate(structure.createdAt)}
                        </p>
                      </div>
                    </div>
                  );
                })
              ) : roomsLoading && displayRooms.length === 0 ? (
                // Rooms are fetched on first switch to this view,
                // so the grid is briefly empty on a project that
                // does have rooms.
                <div className="documents-placeholder">
                  <p>Loading rooms…</p>
                </div>
              ) : (
                // Show individual rooms sorted by structure
                displayRooms
                  .sort((a, b) => {
                    // Get the structure names for sorting
                    const structureA = displayStructures.find((s) =>
                      roomBelongsToStructure(a, s)
                    );
                    const structureB = displayStructures.find((s) =>
                      roomBelongsToStructure(b, s)
                    );

                    const structureNameA =
                      structureA?.name ||
                      structureA?.structureName ||
                      'Unnamed Scan';
                    const structureNameB =
                      structureB?.name ||
                      structureB?.structureName ||
                      'Unnamed Scan';

                    // First sort by structure name
                    const structureCompare =
                      structureNameA.localeCompare(structureNameB);

                    if (structureCompare !== 0) {
                      return structureCompare;
                    }

                    // Then sort by room name within the same structure
                    const nameA = getRoomType(a);
                    const nameB = getRoomType(b);
                    return nameA.localeCompare(nameB);
                  })
                  .map((room) => (
                    <div
                      key={room.id}
                      className="structure-card room-card"
                      onClick={(e) => handleRoomClick(room, e)}
                    >
                      {canEditProject && (
                        <div className="room-menu-container">
                          <button
                            className="room-menu-button"
                            onClick={(e) => handleRoomMenuToggle(room.id, e)}
                            aria-label="Room menu"
                          >
                            <svg
                              width="20"
                              height="20"
                              viewBox="0 0 24 24"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <circle
                                cx="12"
                                cy="5"
                                r="1.5"
                                fill="currentColor"
                              />
                              <circle
                                cx="12"
                                cy="12"
                                r="1.5"
                                fill="currentColor"
                              />
                              <circle
                                cx="12"
                                cy="19"
                                r="1.5"
                                fill="currentColor"
                              />
                            </svg>
                          </button>
                          {menuOpenRoomId === room.id && (
                            <div className="room-menu-dropdown">
                              <button
                                className="menu-item"
                                onClick={(e) => handleRoomRename(room, e)}
                              >
                                <svg
                                  width="16"
                                  height="16"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  xmlns="http://www.w3.org/2000/svg"
                                >
                                  <path
                                    d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  />
                                </svg>
                                Rename
                              </button>
                            </div>
                          )}
                        </div>
                      )}
                      <div className="structure-card-header">
                        <h3>{getRoomType(room)}</h3>
                        <div className="room-meta-info">
                          <p className="room-structure-name">
                            {getStructureNameForRoom(room)}
                          </p>
                          <p className="room-debug-category">
                            Type:{' '}
                            {room.sections && room.sections.length > 0
                              ? room.sections
                                  .map(
                                    (section) =>
                                      section.label ||
                                      section.category ||
                                      'unknown'
                                  )
                                  .join(', ')
                              : 'none'}
                          </p>
                        </div>
                      </div>
                      {room.thumbnailUrl ? (
                        <img
                          src={room.thumbnailUrl}
                          alt={roomDisplayName(room.name)}
                          className="structure-thumbnail"
                        />
                      ) : (
                        <div className="structure-thumbnail-placeholder">
                          <svg
                            viewBox="0 0 24 24"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <rect
                              x="3"
                              y="3"
                              width="18"
                              height="18"
                              rx="2"
                              stroke="currentColor"
                              strokeWidth="2"
                            />
                            <path
                              d="M3 9H21"
                              stroke="currentColor"
                              strokeWidth="2"
                            />
                            <path
                              d="M9 3V21"
                              stroke="currentColor"
                              strokeWidth="2"
                            />
                          </svg>
                        </div>
                      )}
                      <div className="structure-meta-info">
                        <p className="structure-date">
                          {formatDate(room.timestamp || room.createdAt)}
                        </p>
                      </div>
                    </div>
                  ))
              )}

              {/* Add New Room Scan Box - only for those who can edit */}
              {canEditProject && (
                <div
                  className="structure-card new-room-scan-card"
                  onClick={() => setShowIOSDialog(true)}
                >
                  <div className="structure-card-header">
                    <h3>Add New Room Scan</h3>
                  </div>
                  <div className="structure-thumbnail-placeholder">
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M12 5V19M5 12H19"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>
                  <div className="structure-meta-info">
                    <p className="structure-date">Use iOS app to scan</p>
                  </div>
                </div>
              )}

              {/* Upload Floor Plan - admin only */}
              {isAdmin && (
                <div
                  className="structure-card new-room-scan-card"
                  onClick={() => floorplanInputRef.current?.click()}
                >
                  <div className="structure-card-header">
                    <h3>Upload Floor Plan</h3>
                  </div>
                  <div className="structure-thumbnail-placeholder">
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M4 16L4 17C4 18.6569 5.34315 20 7 20L17 20C18.6569 20 20 18.6569 20 17L20 16M16 8L12 4M12 4L8 8M12 4L12 16"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>
                  <div className="structure-meta-info">
                    <p className="structure-date">
                      {floorplanUploadStatus || 'Extract from image'}
                    </p>
                  </div>
                  <input
                    ref={floorplanInputRef}
                    type="file"
                    accept="image/*,.dxf"
                    className="visually-hidden-file"
                    onChange={handleFloorplanUpload}
                  />
                </div>
              )}

              {/* Upload paired floor plans - admin only */}
              {isAdmin && (
                <div
                  className="structure-card new-room-scan-card"
                  onClick={() => setShowMergeModal(true)}
                >
                  <div className="structure-card-header">
                    <h3>Upload paired floor plans</h3>
                  </div>
                  <div className="structure-thumbnail-placeholder">
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M4 7h7M4 12h7M4 17h7M20 7h-5M20 12h-5M20 17h-5M14 4v16"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>
                  <div className="structure-meta-info">
                    <p className="structure-date">
                      Upload plan + dimensions pair
                    </p>
                  </div>
                </div>
              )}
            </div>
          );
        })()}
      </section>

      <section className="content-section">
        <Heading level={3} tone="dark">
          Renderings
        </Heading>
        <AIRenderingsGrid
          structures={structures}
          isViewOnly={!canEditProject}
        />
      </section>
      <IOSAppDialog
        isOpen={showIOSDialog}
        onClose={() => setShowIOSDialog(false)}
        type="room"
      />

      <IFCExportDialog
        isOpen={ifcExportDialog.isOpen}
        structureName={ifcExportDialog.structureName}
        onExport={handleIFCExportConfirm}
        onCancel={() =>
          setIfcExportDialog({
            isOpen: false,
            structureId: null,
            structureName: null,
          })
        }
      />

      <DXFExportDialog
        isOpen={dxfExportDialog.isOpen}
        structureName={dxfExportDialog.structureName}
        onExport={handleDXFExportConfirm}
        onCancel={() =>
          setDxfExportDialog({
            isOpen: false,
            structureId: null,
            structureName: null,
          })
        }
      />

      {showPreTransformedViewer && selectedStructureId && (
        <PreTransformedViewer
          structureId={selectedStructureId}
          roomId={selectedRoomId}
          projectId={id}
          onClose={() => {
            setShowPreTransformedViewer(false);
            setSelectedStructureId(null);
            setSelectedRoomId(null);
          }}
        />
      )}

      <RenameDialog
        isOpen={scanRenameDialog.isOpen}
        onConfirm={handleConfirmScanRename}
        onCancel={() =>
          setScanRenameDialog({ isOpen: false, scanId: null, scanName: null })
        }
        title="Rename Scan"
        currentName={scanRenameDialog.scanName}
        placeholder="Enter new scan name"
      />

      <ConfirmDialog
        isOpen={scanDeleteDialog.isOpen}
        onConfirm={
          scanDeleteDialog.isOwner
            ? handleConfirmScanDelete
            : handleCancelScanDelete
        }
        onCancel={handleCancelScanDelete}
        title={scanDeleteDialog.isOwner ? 'Delete Scan' : 'Permission Denied'}
        message={
          scanDeleteDialog.isOwner ? (
            <div className="scan-delete-confirm">
              <p>
                Are you sure you want to delete "{scanDeleteDialog.scanName}"?
              </p>
              <p className="scan-delete-confirm__lead">This scan contains:</p>
              <ul className="scan-delete-confirm__list">
                <li>
                  {scanDeleteDialog.roomCount} room
                  {scanDeleteDialog.roomCount !== 1 ? 's' : ''}
                </li>
                <li>
                  {scanDeleteDialog.elementCount} element
                  {scanDeleteDialog.elementCount !== 1 ? 's' : ''} (walls,
                  floors, objects)
                </li>
              </ul>
              <p className="scan-delete-confirm__warning">
                This action cannot be undone.
              </p>
            </div>
          ) : (
            <div className="scan-delete-confirm">
              <p>
                You don't have permission to delete "{scanDeleteDialog.scanName}
                ".
              </p>
              <p className="scan-delete-confirm__note">
                Only the owner of this scan can delete it.
              </p>
            </div>
          )
        }
        confirmText={scanDeleteDialog.isOwner ? 'Delete' : 'OK'}
        cancelText={scanDeleteDialog.isOwner ? 'Cancel' : ''}
        confirmButtonStyle={scanDeleteDialog.isOwner ? 'danger' : 'primary'}
      />

      <RenameDialog
        isOpen={roomRenameDialog.isOpen}
        onConfirm={handleConfirmRoomRename}
        onCancel={() =>
          setRoomRenameDialog({ isOpen: false, roomId: null, roomName: null })
        }
        title="Rename Room"
        currentName={roomRenameDialog.roomName}
        placeholder="Enter new room name"
      />

      <MergeAnnotatedFloorplanModal
        isOpen={showMergeModal}
        onClose={() => setShowMergeModal(false)}
        projectId={id}
        onComplete={(result) => {
          setShowMergeModal(false);
          navigate(`/structure/${result.structureId}`);
        }}
      />

      {planChoice && (
        <ChooseFloorPlanModal
          plans={planChoice.plans}
          onChoose={(index) => {
            planChoice.resolve(index);
            setPlanChoice(null);
          }}
          onCancel={() => {
            planChoice.resolve(null);
            setPlanChoice(null);
          }}
        />
      )}
    </>
  );
}
